/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { memo } from "react";
import type {
  FunctionComponent,
  JSX,
  KeyboardEvent,
  MouseEvent,
  ReactNode
} from "react";

import { selectIsTvDevice } from "../../../viewModel/device";
// Sibling barrels rather than `../../common` — importing the aggregate barrel
// from inside it would be circular.
import { Badge } from "../Badge";
import { ContentDescription } from "../ContentDescription";
import { ImageLoader } from "../ImageLoader";
import { TileBadges } from "../TileBadges";
import { ContentCardBadgePlacement } from "./ContentCardProps";
import type {
  ContentCardProps,
  TContentCardAction,
  TContentCardModel
} from "./ContentCardProps";
import "./ContentCard.scss";

/** Separator rendered between metadata fragments. */
const META_SEPARATOR: string = "·";

/** `KeyboardEvent.key` values that activate the card surface. */
const KEY_ENTER: string = "Enter";
const KEY_SPACE: string = " ";

/** Sentinel meaning "focus is on the card body, not on an action". */
const NO_ACTION_FOCUSED: number = -1;

/** Default clamp for `item.description`. */
const DEFAULT_DESCRIPTION_MAX_LINES: number = 2;

/**
 * Drops empty and whitespace-only metadata fragments so the card never renders
 * a dangling separator. Extracted to keep the component body flat.
 *
 * @param metadata - Raw fragments supplied by the caller.
 * @returns Only the fragments with visible content.
 */
const filterMetadata = (metadata: readonly string[]): string[] => {
  return metadata.filter((fragment: string): boolean => {
    return fragment !== undefined && fragment.trim() !== "";
  });
};

/**
 * Clamps a progress percentage into the `0`–`100` range the progress sliver can
 * actually render.
 *
 * @param progressPercent - Raw percentage, possibly out of range or undefined.
 * @returns A percentage between `0` and `100`; `0` means "do not render".
 */
const resolveProgressPercent = (progressPercent?: number): number => {
  if (progressPercent === undefined || Number.isNaN(progressPercent)) {
    return 0;
  }
  return Math.min(100, Math.max(0, progressPercent));
};

/**
 * ContentCard — a reusable horizontal card: 16:9 thumbnail on the left, title,
 * subtitle, an optional highlighted accent line, metadata and description on the
 * right, and a row of action buttons (play / info / delete / unsave) revealed on
 * focus. Actions render as icon-only circles by default, or as icon-plus-label
 * pills when the action sets `hasVisibleLabel`.
 *
 * Designed as a `renderItem` payload for {@link ContentGrid}, but equally usable
 * inside a plain list or panel — it sizes itself to 100% of its parent and takes
 * no feed or domain type, only the presentation-ready {@link TContentCardModel}.
 * Callers holding a `TCarouselItem` should map it with `toContentCardModel`.
 *
 * Focus state (D-pad navigation) is managed externally — the parent screen's
 * Redux slice tracks `isFocused` and `focusedActionIndex` and passes them as
 * props. The card holds no state and never calls `element.focus()`. It is the
 * single tab stop: action buttons carry `tabIndex={-1}`, and Left/Right movement
 * between them is routed by the screen's `ScreenController`.
 *
 * The action row stays mounted at all times and reveals via `opacity` /
 * `transform` so showing it never reflows the card inside a fixed-height grid
 * cell.
 */
const ContentCardComponent: FunctionComponent<ContentCardProps> = (
  props: ContentCardProps
): JSX.Element => {
  // a. TAG
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars, sonarjs/no-dead-store -- Required by CodingGuidelines §7 (D-09) for logging context
  const TAG: string = "ContentCard";

  // b. Local constants
  const {
    item,
    actions = [],
    isFocused = false,
    focusedActionIndex = NO_ACTION_FOCUSED,
    onFocus,
    onSelect,
    onActionSelect,
    areActionsAlwaysVisible = false,
    descriptionMaxLines = DEFAULT_DESCRIPTION_MAX_LINES,
    className = "",
    dataTestId = "content-card"
  } = props;

  // c. Local shared variables
  const model: TContentCardModel = item;
  const hasImage: boolean =
    model.imageUrl !== undefined && model.imageUrl !== "";
  const metadata: string[] =
    model.metadata === undefined ? [] : filterMetadata(model.metadata);
  const progressPercent: number = resolveProgressPercent(model.progressPercent);
  const badgePlacement: ContentCardBadgePlacement =
    model.badgePlacement === undefined
      ? ContentCardBadgePlacement.TOP_RIGHT
      : model.badgePlacement;
  const hasActions: boolean = actions.length > 0;
  const areActionsVisible: boolean = areActionsAlwaysVisible || isFocused;

  const rootClassName: string =
    "content-card" +
    (isFocused ? " content-card--focused" : "") +
    (className === "" ? "" : " " + className);

  const actionsClassName: string =
    "content-card__actions" +
    (areActionsVisible ? " content-card__actions--visible" : "");

  // g. Internal functions
  /**
   * Mirrors the activation a native `<button>` would give the surface, so the
   * card is operable by keyboard as well as pointer. D-pad OK is still routed by
   * the screen's `ScreenController` — this only covers browser keyboards.
   */
  const handleSurfaceKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const isActivationKey: boolean =
      event.key === KEY_ENTER || event.key === KEY_SPACE;
    if (isActivationKey && onSelect !== undefined) {
      // Space would otherwise scroll the page.
      event.preventDefault();
      onSelect();
    }
  };

  /**
   * Pointer-driven focus — browser only, and reported on *movement* rather than
   * on `mouseenter`.
   *
   * `mouseenter` fires whenever the element under the pointer changes — including
   * when the pointer never moved and the list scrolled beneath it. D-pad focus
   * scrolls the row into view, which slides a different card under a resting
   * desktop cursor, and that phantom enter dispatched `onFocus` for *that* card:
   * pressing Down on the last visible row moved focus on, the region scrolled,
   * and the action row then appeared on whichever card had drifted under the
   * mouse instead of on the row the user had actually navigated to.
   *
   * `mousemove` only fires for real pointer motion, so a scroll can no longer
   * steal focus. The guard keeps it to one dispatch per card: the event repeats
   * for every pixel of travel, and the card is already focused after the first.
   */
  const handlePointerMove = (): void => {
    if (selectIsTvDevice()) {
      return;
    }
    if (!isFocused && onFocus !== undefined) {
      onFocus();
    }
  };

  const handleActionClick = (
    event: MouseEvent<HTMLButtonElement>,
    action: TContentCardAction,
    index: number
  ): void => {
    // Keep an action tap from also activating the card behind it.
    event.stopPropagation();
    if (onActionSelect !== undefined) {
      onActionSelect(String(action.id), index);
    }
  };

  const renderAction = (
    action: TContentCardAction,
    index: number
  ): JSX.Element => {
    const isActionFocused: boolean = index === focusedActionIndex;
    const isActionDisabled: boolean = action.isDisabled === true;
    const hasVisibleLabel: boolean = action.hasVisibleLabel === true;
    const actionClassName: string =
      "content-card__action" +
      (hasVisibleLabel ? " content-card__action--labeled" : "") +
      (action.isPrimary === true ? " content-card__action--primary" : "") +
      (isActionFocused ? " content-card__action--focused" : "") +
      (isActionDisabled ? " content-card__action--disabled" : "");

    return (
      <button
        key={String(action.id)}
        className={actionClassName}
        type="button"
        tabIndex={-1}
        // The label is the accessible name either way: when it is visible the
        // aria-label simply matches it, and when it is not it is the only name
        // the button has.
        aria-label={action.label}
        title={action.label}
        disabled={isActionDisabled}
        onClick={(event: MouseEvent<HTMLButtonElement>): void => {
          handleActionClick(event, action, index);
        }}
      >
        {action.icon}
        {hasVisibleLabel && (
          <span className="content-card__action-label">{action.label}</span>
        )}
      </button>
    );
  };

  const renderThumbnail = (): ReactNode => (
    <div className="content-card__frame">
      {hasImage && (
        <ImageLoader
          src={model.imageUrl}
          fallbackText={model.title}
          classStyle="content-card__thumb-image"
        />
      )}
      {!hasImage && (
        <div className="content-card__fallback">
          <span className="content-card__fallback-title">{model.title}</span>
        </div>
      )}
      {/*
        The same badge strip a carousel tile carries, in the same priority
        order — a row and a tile for one recording have to read the same. It
        renders nothing when the item carries no flags, so it is unconditional
        here. Recording is the red dot inside it, which is why the card no
        longer draws a dot of its own.
      */}
      <TileBadges
        isRecording={model.isRecording}
        isLive={model.isLive}
        isUpcoming={model.isUpcoming}
        isNew={model.isNew}
        isSaved={model.isSaved}
        isWatched={model.isWatched}
        classStyle="content-card__badges"
      />
      {model.badgeText !== undefined && model.badgeText !== "" && (
        <Badge
          text={model.badgeText}
          classStyle={
            "content-card__badge content-card__badge--" + badgePlacement
          }
        />
      )}
      {progressPercent > 0 && (
        <div className="content-card__progress">
          <div
            className="content-card__progress-fill"
            style={{ width: String(progressPercent) + "%" }}
          />
        </div>
      )}
    </div>
  );

  const renderMetadata = (): ReactNode => (
    <div className="content-card__meta">
      {metadata.map((fragment: string, index: number): JSX.Element => {
        return (
          <span key={fragment + "-" + String(index)}>
            {index > 0 && (
              <span className="content-card__meta-separator">
                {META_SEPARATOR + " "}
              </span>
            )}
            {fragment}
          </span>
        );
      })}
    </div>
  );

  // i. Return JSX
  // Two columns: the thumbnail, then a text-plus-actions column beside it. The
  // action row is a sibling of `__surface`, never a descendant: nesting
  // interactive controls inside another interactive element is invalid and would
  // make every action tap also register as a card activation. It has to sit in
  // the *text* column rather than under the whole card, because Figma
  // (191:707) puts the buttons directly under the accent line — level with the
  // thumbnail, not below it.
  //
  // The thumbnail is therefore outside `__surface` and mirrors its pointer
  // handlers, so clicking or hovering the poster art still selects the row. It
  // is deliberately not a second tab stop: `__surface` remains the card's single
  // focusable layer and already carries the title as its accessible name.
  return (
    <div className={rootClassName} data-testid={dataTestId}>
      {/*
        Pointer-only, by design: keyboard and D-pad activation live on
        `__surface`, the card's single tab stop. A second tab stop here would
        make every card two stops for no gain — the surface already carries the
        title as its accessible name. Hover focus comes from `mousemove`, not
        `mouseenter` — see handlePointerMove.

        `role="presentation"` states that intent: the poster art duplicates an
        affordance that `__surface` already exposes accessibly, so it is a
        pointer shortcut rather than a control of its own and carries no
        keyboard handler (SonarQube S6848 / S1082).
      */}
      <div
        className="content-card__thumb"
        role="presentation"
        onMouseMove={handlePointerMove}
        onClick={onSelect}
      >
        {renderThumbnail()}
      </div>
      <div className="content-card__column">
        {/*
          role="button" rather than a native <button> (SonarQube S6819,
          suppressed for this file in sonar-project.properties): a <button>
          admits only phrasing content, but this surface wraps
          ContentDescription's <p>. tabIndex + onKeyDown below supply the same
          keyboard semantics without invalid markup.
        */}
        <div
          className="content-card__surface"
          role="button"
          tabIndex={0}
          aria-label={model.title}
          onFocus={onFocus}
          onMouseMove={handlePointerMove}
          onClick={onSelect}
          onKeyDown={handleSurfaceKeyDown}
        >
          <div className="content-card__body">
            <h3 className="content-card__title">{model.title}</h3>
            {model.subtitle !== undefined && model.subtitle !== "" && (
              <span className="content-card__subtitle">{model.subtitle}</span>
            )}
            {model.accentText !== undefined && model.accentText !== "" && (
              <span className="content-card__accent">{model.accentText}</span>
            )}
            {metadata.length > 0 && renderMetadata()}
            {model.description !== undefined && model.description !== "" && (
              <ContentDescription
                text={model.description}
                maxLines={descriptionMaxLines}
                classStyle="content-card__description"
              />
            )}
          </div>
        </div>
        {hasActions && (
          <div className={actionsClassName} aria-hidden={!areActionsVisible}>
            {actions.map(
              (action: TContentCardAction, index: number): JSX.Element =>
                renderAction(action, index)
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Memoized so D-pad traversal through a grid only re-renders the cards whose
 * focus state actually changed. Default shallow comparison — callers must pass
 * stable `actions` / callback references (`useMemo` / `useCallback`).
 */
const ContentCard = memo(ContentCardComponent);

export default ContentCard;
